import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface PublicNavbarProps {
  logoText?: string;
}

const PublicNavbar: React.FC<PublicNavbarProps> = ({ logoText = 'StitchFlo' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path ? 
      { 
        borderBottom: '2px solid',
        borderColor: 'primary.main',
        borderRadius: 0,
        color: 'primary.main',
        fontWeight: 600
      } : {};
  };

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleNavigation = (action: () => void) => {
    if (isMobile) {
      setDrawerOpen(false);
    }
    action();
  };

  const navItems = [
    {
      label: 'Home',
      onClick: () => navigate('/'),
      isActive: location.pathname === '/'
    },
    {
      label: 'Features',
      onClick: () => {
        if (location.pathname === '/') {
          const featuresSection = document.getElementById('features-section');
          if (featuresSection) {
            featuresSection.scrollIntoView({ behavior: 'smooth' });
          }
        } else {
          navigate('/', { state: { scrollToFeatures: true } });
        }
      },
      isActive: false
    },
    {
      label: 'Pricing',
      onClick: () => {
        if (location.pathname === '/') {
          const pricingSection = document.getElementById('pricing-section');
          if (pricingSection) {
            pricingSection.scrollIntoView({ behavior: 'smooth' });
          }
        } else {
          navigate('/', { state: { scrollToPricing: true } });
        }
      },
      isActive: false
    },
    {
      label: 'Contact Us',
      onClick: () => {
        if (location.pathname === '/') {
          const contactSection = document.getElementById('contact-section');
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth' });
          }
        } else {
          navigate('/', { state: { scrollToContact: true } });
        }
      },
      isActive: false
    }
  ];

  // Mobile drawer content
  const drawer = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
          {logoText}
        </Typography>
      </Box>
      <Divider />
      <List>
        {navItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton 
              onClick={() => handleNavigation(item.onClick)} 
              selected={item.isActive}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() => {
            setDrawerOpen(false);
            navigate('/login');
          }}
        >
          {location.pathname === '/login' ? 'Login' : 'Sign In'}
        </Button>
      </Box>
    </Box>
  );

  return (
    <AppBar position="static" color="transparent" elevation={0}>
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          {/* Logo */}
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              flexGrow: { xs: 1, md: 0 },
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
          >
            {logoText}
          </Typography>

          {/* Mobile Menu Icon */}
          {isMobile && (
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          {/* Desktop Navigation Links */}
          {!isMobile && (
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, md: 2 },
              alignItems: 'center'
            }}>
              {navItems.map((item, index) => (
                <Button 
                  key={index}
                  color="inherit" 
                  sx={{ 
                    ...(item.isActive ? isActive('/') : {}),
                    px: { xs: 1, md: 2 }
                  }}
                  onClick={item.onClick}
                >
                  {item.label}
                </Button>
              ))}
              
              <Button
                variant={location.pathname === '/login' ? 'outlined' : 'contained'}
                color="primary"
                onClick={() => navigate('/login')}
                sx={{ 
                  ml: { xs: 1, md: 2 },
                  px: { xs: 2, md: 3 }
                }}
              >
                {location.pathname === '/login' ? 'Login' : 'Sign In'}
              </Button>
            </Box>
          )}
          
          {/* Mobile Drawer */}
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
          >
            {drawer}
          </Drawer>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default PublicNavbar; 